// =============================================================================
// buildTokens.ts — tenant branding → resolved design tokens (FORT-180)
//
// This is the ONE place tenant branding (`BrandingSettings`) is read and folded
// onto the base design tokens. Components consume the resolved tokens via
// `useTokens()` (see TokensProvider) and never read `branding.*` colours
// directly — so swapping the UI library or building a mobile client only needs
// to re-bind the primitives to tokens, not re-implement branding plumbing.
//
// Hex validation lives here too (moved from App.tsx) as the single source of
// truth. Branding colours are tenant-controlled in a multi-tenant system and are
// interpolated into <style> blocks / theme tokens, so a malformed value (e.g.
// `red; } body { display:none }`) must never reach the DOM. `safeHexColor`
// rejects anything that isn't a strict 6-digit hex, matching the server-side
// regex in ClientService.cs and the ThemeSection form validation.
// =============================================================================

import { tokens } from './tokens';
import type { BrandingSettings } from '../../model/client/ClientSettings';

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export const safeHexColor = (value: string | undefined | null, fallback: string): string =>
  typeof value === 'string' && HEX_COLOR_RE.test(value) ? value : fallback;

/**
 * Readable text colour for a branded surface.
 *
 * Sider chrome and anything else painted with a branding colour can be any valid hex, so
 * hardcoding white text is only safe until a tenant picks a pale brand — at which point the
 * text disappears. Uses the WCAG relative-luminance formula and its 0.179 threshold, which
 * is the point where black overtakes white for contrast against the same background.
 *
 * A malformed value returns white, matching what the app did before this existed.
 */
export function contrastTextOn(background: string): '#ffffff' | '#000000' {
  if (!HEX_COLOR_RE.test(background)) return '#ffffff';
  const channel = (start: number) => {
    const srgb = parseInt(background.slice(start, start + 2), 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

/**
 * Resolve tenant branding onto the base tokens. Only colour tokens are
 * branding-overridable today (primary + the sider chrome); everything else
 * (spacing, type, radii, shadows) passes through unchanged. Each override
 * preserves the exact fallback chain the components used before this layer
 * existed — the only behavioural change is that an invalid (non-hex) override
 * is now rejected rather than passed through, which is strictly safer.
 */
export function buildTokens(branding: BrandingSettings) {
  const primary = safeHexColor(branding.primaryColor, tokens.color.primary);
  return {
    ...tokens,
    color: {
      ...tokens.color,
      primary,
      sidebar: {
        // Sider background: tenant sidebarBg → primary → base default,
        // matching the old `branding.sidebarBg || branding.primaryColor || …`.
        bg: safeHexColor(branding.sidebarBg, primary),
        submenuBg: safeHexColor(branding.submenuBg, tokens.color.sidebar.submenuBg),
        itemSelectedBg: safeHexColor(branding.sidebarItemSelectedBg, tokens.color.sidebar.itemSelectedBg),
      },
    },
  };
}

/** Tenant-resolved tokens, as returned by buildTokens / useTokens. */
export type Theme = ReturnType<typeof buildTokens>;
